import React, {PureComponent} from "react";
import withStyles from '@mui/styles/withStyles';
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Grow from "@mui/material/Grow";
import Collapse from "@mui/material/Collapse";
import ButtonBase from "@mui/material/ButtonBase";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {UrlBuilder} from "../../../core/url/UrlBuilder";

const BADGE_ICONS = {
	// Consistency
	seedling: "🌱", fire: "🔥", muscle: "💪", pillar: "🏛️", star: "⭐", trident: "🔱",
	crown: "👑", diamond: "💎", infinity: "♾️",
	// Streak
	link: "🔗", chain: "⛓️", mountain: "🏔️", rocket: "🚀",
	hourglass: "⏳", calendar: "📅",
	// Strength
	bronze_medal: "🥉", silver_medal: "🥈", gold_medal: "🥇",
	// Volume
	rock: "🏋️", truck: "🚛", anchor: "⚓", crane: "🏗️", factory: "🏭", globe: "🌍", volcano: "🌋",
	// Cardio
	footprints: "👣", shoe: "👟", road: "🛤️", map: "🗺️", earth: "🌏",
	heart: "❤️", heartbeat: "💓", wind: "🌬️",
	// Variety
	compass: "🧭", wheel: "🎡", books: "📚", scroll: "📜",
	// Dedication
	stopwatch: "⏱️", clock: "🕐", runner: "🏃", shield: "🛡️", hammer: "🔨", temple: "⛩️",
	// Milestones
	sunrise: "🌅", moon: "🌙", stars: "🌠", lightning: "⚡", dash: "💨",
	hourglass_full: "⌛", repeat: "🔁", three: "🏅",
	hundred: "💯", one: "☝️", hand: "🖐️", dot: "•", phoenix: "🔄",
	share: "📤", loop: "🔁",
	baby: "👶", pencil: "✏️", mirror: "🪞", brain: "🧠",
	gauge: "📏", clipboard: "📋", explosion: "💥",
	// Journey
	badge_1: "1️⃣", badge_2: "2️⃣", badge_3: "3️⃣", badge_4: "4️⃣", badge_5: "5️⃣",
};

const useStyles = theme => ({
	container: {
		position: 'relative',
		overflow: 'hidden',
		borderRadius: 12,
		background: 'linear-gradient(135deg, #7C4DFF 0%, #536DFE 50%, #448AFF 100%)',
		padding: theme.spacing(2),
		marginBottom: theme.spacing(2),
		boxShadow: '0 4px 20px rgba(124, 77, 255, 0.4)',
	},
	header: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		gap: theme.spacing(1),
	},
	title: {
		color: '#fff',
		fontWeight: 700,
		textAlign: 'center',
	},
	badgeRow: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1.5),
		padding: theme.spacing(0.75, 0),
	},
	badgeIcon: {
		fontSize: 28,
		width: 40,
		height: 40,
		borderRadius: '50%',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: 'rgba(255,255,255,0.18)',
		border: '2px solid rgba(255,255,255,0.3)',
		filter: 'saturate(0.6)',
	},
	badgeName: {
		color: '#fff',
		fontWeight: 600,
	},
	badgeDesc: {
		color: 'rgba(255,255,255,0.8)',
	},
	expandButton: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '100%',
		paddingTop: theme.spacing(0.5),
	},
	expandLabel: {
		color: 'rgba(255,255,255,0.8)',
		fontSize: '0.8rem',
	},
	expandIcon: {
		color: 'rgba(255,255,255,0.8)',
		fontSize: 18,
	},
});

class AchievementUnlockedBanner extends PureComponent {
	constructor(props) {
		super(props);
		this.state = {
			newBadges: [],
			isLoading: true,
			expanded: false,
		};
	}

	componentDidMount() {
		if (this.props.justFinished) {
			this.checkForNewBadges();
		}
	}

	checkForNewBadges = async () => {
		try {
			const response = await fetch(UrlBuilder.user.achievementsCheckApi());
			if (response.status !== 200) {
				this.setState({isLoading: false});
				return;
			}
			const responseData = await response.json();
			this.setState({
				newBadges: responseData.data || [],
				isLoading: false,
			});
		} catch {
			this.setState({isLoading: false});
		}
	}

	toggleExpanded = () => {
		this.setState(prev => ({expanded: !prev.expanded}));
	}

	render() {
		const {classes} = this.props;
		const {newBadges, isLoading} = this.state;

		if (isLoading || !newBadges || newBadges.length === 0) {
			return null;
		}

		const firstBadge = newBadges[0];
		const hasMultiple = newBadges.length > 1;

		return (
			<Grow in={true} timeout={800}>
				<div className={classes.container}>
					<div className={classes.header}>
						<Typography variant="h6" className={classes.title}>
							🎖️ Achievement Unlocked!
						</Typography>
					</div>

					<Box mt={1.5}>
						<div className={classes.badgeRow}>
							<div className={classes.badgeIcon}>
								{BADGE_ICONS[firstBadge.icon] || '🏅'}
							</div>
							<div>
								<Typography variant="subtitle2" className={classes.badgeName}>
									{firstBadge.name}
								</Typography>
								<Typography variant="caption" className={classes.badgeDesc}>
									{firstBadge.description}
								</Typography>
							</div>
						</div>
					</Box>

					{hasMultiple && (
						<>
							<ButtonBase className={classes.expandButton} onClick={this.toggleExpanded}>
								<Typography className={classes.expandLabel}>
									{this.state.expanded ? 'Hide' : `+${newBadges.length - 1} more`}
								</Typography>
								{this.state.expanded
									? <ExpandLessIcon className={classes.expandIcon}/>
									: <ExpandMoreIcon className={classes.expandIcon}/>
								}
							</ButtonBase>

							<Collapse in={this.state.expanded}>
								{newBadges.slice(1).map(badge =>
									<div className={classes.badgeRow} key={badge.key}>
										<div className={classes.badgeIcon}>
											{BADGE_ICONS[badge.icon] || '🏅'}
										</div>
										<div>
											<Typography variant="subtitle2" className={classes.badgeName}>
												{badge.name}
											</Typography>
											<Typography variant="caption" className={classes.badgeDesc}>
												{badge.description}
											</Typography>
										</div>
									</div>
								)}
							</Collapse>
						</>
					)}
				</div>
			</Grow>
		);
	}
}

export default withStyles(useStyles)(AchievementUnlockedBanner);
